import {AgentType, LLMRole} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ChatFileUpload} from 'llm/chat-library/ChatLibraryTypes';
import type {ProgressData} from 'llm/chat-library/progress/ProgressTypes';
import {assertNever} from 'util/Types';

import {
  ClientChatEvent,
  ClientDisplayChatEvent,
  ClientOptimisticUserMessage,
  ClientProgressMessage,
  ClientStreamingAssistantMessage,
  DisplayMessageAssistantResponseType,
  DisplayMessageChatEventNode,
  ProgressStatus,
} from './MessageTypes';

export function filterDisplayEvents(events: Array<ClientChatEvent>): Array<ClientDisplayChatEvent> {
  return events.filter(
    (event): event is ClientDisplayChatEvent =>
      event.__typename === 'DisplayMessageChatEventNode' ||
      event.__typename === 'ClientOptimisticUserMessage' ||
      event.__typename === 'ClientProgressMessage' ||
      event.__typename === 'ClientStreamingAssistantMessage'
  );
}

export function getDisplayMessageAssistantResponseType(
  event: DisplayMessageChatEventNode | ClientStreamingAssistantMessage
): DisplayMessageAssistantResponseType {
  // TODO: This should be derived from the event type, not agent type.
  switch (event.agentType) {
    case AgentType.DEEP_RESEARCH:
      return DisplayMessageAssistantResponseType.REPORT;
    case AgentType.ASK:
      return DisplayMessageAssistantResponseType.REPLY;
    case AgentType.DATA_CAPTURE:
      return DisplayMessageAssistantResponseType.REPLY;
    case AgentType.LEGACY_DATA_IMPORT:
      return DisplayMessageAssistantResponseType.REPLY;
    case AgentType.ANALYSIS_AGENT:
      return DisplayMessageAssistantResponseType.REPLY;
    default:
      assertNever(event.agentType);
      return DisplayMessageAssistantResponseType.REPLY;
  }
}

export function createProgressMessage({
  status,
  errorMessage,
  agentType,
  progressData,
  associatedMessageId,
}: {
  status: ProgressStatus;
  errorMessage: string | null;
  agentType: AgentType | null;
  progressData: ProgressData | null;
  associatedMessageId?: string;
}): ClientProgressMessage {
  const message: ClientProgressMessage = {
    __typename: 'ClientProgressMessage',
    status,
    errorMessage,
    agentType,
    role: null,
    progressData,
  };
  // Only include associatedMessageId if provided (for completed thinking sections)
  if (associatedMessageId !== undefined) {
    message.associatedMessageId = associatedMessageId;
  }
  return message;
}

export function createStreamingMessage({
  createdAt,
  agentType,
  responseToEventId,
  streamingContent,
}: {
  createdAt: string;
  agentType: AgentType;
  responseToEventId: string;
  streamingContent: string;
}): ClientStreamingAssistantMessage {
  return {
    __typename: 'ClientStreamingAssistantMessage',
    responseToEventId,
    messageBlocks: [
      {
        __typename: 'DisplayTextBlockNode',
        text: streamingContent,
      },
    ],
    role: LLMRole.ASSISTANT,
    agentType,
    createdAt,
  };
}

export function createPendingUserMessage({
  text,
  fileUploads,
  agentType,
}: {
  text: string;
  fileUploads: Array<ChatFileUpload>;
  agentType: AgentType;
}): ClientOptimisticUserMessage {
  return {
    __typename: 'ClientOptimisticUserMessage',
    role: LLMRole.USER,
    agentType,
    messageBlocks: [
      {
        __typename: 'DisplayTextBlockNode',
        text,
      },
      ...fileUploads.map((fileUpload) => ({
        __typename: 'DisplayFileBlockNode' as const,
        filename: fileUpload.filename,
        mediaType: fileUpload.mediaType,
      })),
    ],
  };
}
